/**
 * BrandingForm Component
 *
 * Form for updating branding settings (company name, colors, logos).
 */

import { useState } from 'react';
import { Upload, Palette, Building2 } from 'lucide-react';
import { Button, buttonVariants, cn } from '@elbklar/ui';
import { TextField } from '@/components/form/Felder';
import { useUpdateBranding, resolveAssetUrl } from '@/hooks/useTenantConfig';

interface TenantConfig {
  company_name: string;
  primary_color: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
}

interface BrandingFormProps {
  config: TenantConfig;
}

export function BrandingForm({ config }: BrandingFormProps) {
  const [companyName, setCompanyName] = useState(config.company_name);
  // Die Primärfarbe ist ein Datum des Mandanten (Branding für Dokumente und
  // Login), kein Theme-Wert der Oberfläche.
  // eslint-disable-next-line keine-festen-farben -- Vorgabe der Mandanten-Markenfarbe, ein Datum
  const [primaryColor, setPrimaryColor] = useState(config.primary_color || '#3B82F6');
  const [logoLight, setLogoLight] = useState<File | null>(null);
  const [logoDark, setLogoDark] = useState<File | null>(null);
  const [favicon, setFavicon] = useState<File | null>(null);

  const updateMutation = useUpdateBranding();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('company_name', companyName);
    formData.append('primary_color', primaryColor);

    if (logoLight) formData.append('logo_light', logoLight);
    if (logoDark) formData.append('logo_dark', logoDark);
    if (favicon) formData.append('favicon', favicon);

    await updateMutation.mutateAsync(formData);
  };

  const hochladen = cn(buttonVariants({ size: 'sm' }), 'cursor-pointer');

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      <TextField
        label="Firmenname"
        icon={Building2}
        type="text"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        placeholder="Elbklar GmbH"
      />

      {/* Primary Color */}
      <div className="flex flex-col gap-1.5">
        <span className="t-body-strong">Primärfarbe</span>
        <div className="flex items-center gap-4">
          <TextField
            aria-label="Primärfarbe als Hex-Wert"
            icon={Palette}
            type="text"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            className="w-44 font-mono"
            // eslint-disable-next-line keine-festen-farben -- Beispielwert im Platzhalter
            placeholder="#3B82F6"
          />
          <input
            type="color"
            aria-label="Primärfarbe wählen"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            className="size-control-md cursor-pointer rounded-md border border-input bg-muted p-0.5"
          />
          <div className="h-control-md w-24 rounded-md border border-border" style={{ backgroundColor: primaryColor }} />
        </div>
      </div>

      {/* Logo Light */}
      <div className="flex flex-col gap-1.5">
        <span className="t-body-strong">Logo (heller Hintergrund)</span>
        <div className="flex items-center gap-4">
          {config.logo_light_url && !logoLight && (
            <img
              src={resolveAssetUrl(config.logo_light_url)}
              alt="Logo Light"
              className="h-12 rounded-md border border-border bg-card object-contain p-2"
            />
          )}
          {logoLight && <span className="rounded-md bg-muted px-3 py-2 t-small text-muted-foreground">{logoLight.name}</span>}
          <label className={hochladen}>
            <Upload size={14} strokeWidth={1.75} aria-hidden />
            {config.logo_light_url ? 'Ändern' : 'Hochladen'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={(e) => setLogoLight(e.target.files?.[0] || null)}
            />
          </label>
        </div>
      </div>

      {/* Logo Dark */}
      <div className="flex flex-col gap-1.5">
        <span className="t-body-strong">Logo (dunkler Hintergrund)</span>
        <div className="flex items-center gap-4">
          {config.logo_dark_url && !logoDark && (
            <img
              src={resolveAssetUrl(config.logo_dark_url)}
              alt="Logo Dark"
              className="h-12 rounded-md border border-border bg-foreground object-contain p-2"
            />
          )}
          {logoDark && <span className="rounded-md bg-muted px-3 py-2 t-small text-muted-foreground">{logoDark.name}</span>}
          <label className={hochladen}>
            <Upload size={14} strokeWidth={1.75} aria-hidden />
            {config.logo_dark_url ? 'Ändern' : 'Hochladen'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={(e) => setLogoDark(e.target.files?.[0] || null)}
            />
          </label>
        </div>
      </div>

      {/* Favicon */}
      <div className="flex flex-col gap-1.5">
        <span className="t-body-strong">Favicon</span>
        <div className="flex items-center gap-4">
          {config.favicon_url && !favicon && (
            <img src={resolveAssetUrl(config.favicon_url)} alt="Favicon" className="size-8 object-contain" />
          )}
          {favicon && <span className="rounded-md bg-muted px-3 py-2 t-small text-muted-foreground">{favicon.name}</span>}
          <label className={hochladen}>
            <Upload size={14} strokeWidth={1.75} aria-hidden />
            {config.favicon_url ? 'Ändern' : 'Hochladen'}
            <input
              type="file"
              accept="image/png,image/x-icon,image/ico"
              className="hidden"
              onChange={(e) => setFavicon(e.target.files?.[0] || null)}
            />
          </label>
        </div>
        <p className="t-small text-muted-foreground">Empfohlen: PNG mit 32 × 32 oder 64 × 64 px</p>
      </div>

      {/* Submit */}
      <div className="flex items-center gap-4 border-t border-border pt-4">
        <Button type="submit" variant="primary" loading={updateMutation.isPending}>
          Speichern
        </Button>
        {updateMutation.isSuccess && <span className="t-small text-success">Gespeichert</span>}
        {updateMutation.isError && (
          <span className="t-small text-danger">{(updateMutation.error as Error)?.message || 'Speichern fehlgeschlagen'}</span>
        )}
      </div>
    </form>
  );
}

export default BrandingForm;
